import { React, useEffect, useState, useMemo, useCallback } from "react";
import { Box, Button, Typography, CircularProgress, Paper, Menu, MenuItem as MuiMenuItem } from "@mui/material";
import SearchBoxComponent from "../../Generals/SearchBox/SearchBoxComponent";
import Button3px from "../../Generals/Buttons/3pxButton.js";
import Colors from "../../../assets/Variables/ColorsVariables";
import SelectorSimple from "../../Generals/Selectors/SelectorSimple";
import NotificationModal from "../../Generals/Notifications/NotificationModal";
import FormSystemModal from "../../Generals/Modals/FormSystemModal";
import adminMethods from "../../../services/admin";
import TapComponent from "../../Generals/TabPanels/TapComponent";
import CustomTapPanelComponent from "../../Generals/TabPanels/CustomTapPanelComponent";
import TableFrontPagination from "../../Generals/Tables/TableFrontPagination";
import ExportComponent from "../../Generals/ExporterFiles/ExporterFileComponent";
import * as Styles from "./Styles.js";
import FilterGroupsChips from "./Filters.js";
import IconComponent from "../../Generals/Icons/IconComponent";
import { useNavigate } from "react-router-dom";
import StatusChip from "../../Generals/Chips/StatusChip.js";
import FollowersPanelSidebar from "../../Generals/Modals/FollowerPanel.js";
import CheckBoxToggle from "../../Generals/Checks/CheckBox";
import CopyLinkSnackbar from "../../Generals/Notifications/CopyLinkSnackbar";
import * as Encryptor from "../../../Tools/AESEncrypter";


const ACTIVITY_OPTIONS = [
  { value: "", label: "All" },
  { value: "EXIST_IN_MT", label: "Exist in MT" },
  { value: "ENABLE_IN_CT", label: "Enable in CT" },
];

const AccountsScreen = () => {
  //Signals Menu
  const handleOpenSignalsMenu = useCallback((event, row) => {
    setAnchorElSignals(event.currentTarget);
    setSelectedSignalRow(row);
  }, []);
  //Followers Menu
  const handleOpenFollowersMenu = useCallback((event, row) => {
    setAnchorElFollowers(event.currentTarget);
    setSelectedFollowerRow(row);
  }, []);
  // Columns for signals are constant to avoid recalculation
  const SIGNAL_COLUMNS = useMemo(() => [
    { field: "login", headerName: "Login", flex: 1 },
    { field: "name", headerName: "Name", flex: 2 },
    { field: "strategy_name", headerName: "Strategy Name", flex: 2 },
    { field: "fee_value_percentage", headerName: "Fee %", flex: 1 },
    { field: "total_followers", headerName: "Total Followers", flex: 1 },
    { field: "drawdown", headerName: "Drawdown", flex: 1, renderCell: (params) => <StatusChip value={params.value} /> },
    { field: "max_drawdown", headerName: "Max Drawdown", flex: 1 },
    { field: "visibility", headerName: "Visibility", flex: 1, renderCell: (params) => <StatusChip value={params.value} /> },
    { field: "available_in_mt", headerName: "Existing in MT", flex: 1, renderCell: (params) => <StatusChip value={params.value} /> },
    { field: "group", headerName: "Group", flex: 2 },
    { field: "currency", headerName: "Currency", flex: 1 },
    { field: "ib_account", headerName: "IB Account", flex: 1 },
    { field: "status_of_ib_account", headerName: "IB State", flex: 1, renderCell: (params) => <StatusChip value={params.value} /> },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Button
          variant="outlined"
          sx={Styles.ActionsButton}
          onClick={(event) => handleOpenSignalsMenu(event, params.row)} // ← Ya es estable
        >
          <IconComponent icon="more_vert" size="12px" />
        </Button>
      )
    }
  ], [handleOpenSignalsMenu]); // Empty array = stable always
  // Columns for followers are also constant
  const FOLLOWER_COLUMNS = useMemo(() => [
    { field: "login", headerName: "Login", flex: 1 },
    { field: "name", headerName: "Name", flex: 2 },
    { field: "signal_login", headerName: "Signal Login", flex: 1 },
    { field: "copy_mode", headerName: "Copy Mode", flex: 1 },
    { field: "risk_ratio", headerName: "Risk Ratio", flex: 1 },
    { field: "min_lot_size", headerName: "Min Lot Size", flex: 1 },
    { field: "max_lot_size", headerName: "Max Lot Size", flex: 1 },
    { field: "drawdown", headerName: "Drawdown Mode", flex: 1, renderCell: (params) => <StatusChip value={params.value} /> },
    { field: "max_drawdown", headerName: "Max Drawdown", flex: 1 },
    { field: "following_date", headerName: "Following Date", flex: 2 },
    { field: "modification_date", headerName: "Modification Date", flex: 2 },
    { field: "visibility_follower", headerName: "Copying", flex: 1, renderCell: (params) => <StatusChip value={params.value} /> },
    { field: "available_in_mt", headerName: "Existing in MT", flex: 1, renderCell: (params) => <StatusChip value={params.value} /> },
    { field: "group", headerName: "Group", flex: 2 },
    { field: "currency", headerName: "Currency", flex: 1 },
    {
      field: "actions", headerName: "Actions", flex: 1, sortable: false, filterable: false,
      renderCell: (params) => (
        <Button
          variant="outlined"
          sx={Styles.ActionsButton}
          onClick={(event) => {
            handleOpenFollowersMenu(event, params.row);
            console.log("Clicked follower action", params.row);
          }}
        >
          <IconComponent icon="more_vert" size="12px" />
          &nbsp;
        </Button>
      )
    },
  ], [handleOpenFollowersMenu]);


  const [loading, setLoading] = useState(false);
  const [, setError] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorElSignals, setAnchorElSignals] = useState(null);
  const [selectedSignalRow, setSelectedSignalRow] = useState(null);
  const [anchorElFollowers, setAnchorElFollowers] = useState(null);
  const [selectedFollowerRow, setSelectedFollowerRow] = useState(null);
  const exportOpen = Boolean(anchorEl);
  const [tabstate, setTabstate] = useState(0); //Signal selected for default
  const [availableGroups, setAvailableGroups] = useState([]);
  const [filterSignal, setFilterSignal] = useState({
    search: "",
    activity: "",
    groups: [],
    exact_search: false,
  })
  const [filterFollower, setFilterFollower] = useState({
    search: "",
    activity: "",
    groups: [],
    exact_search: false,
  })
  const [rowsSignal, setRowsSignal] = useState([]);
  const [rowsFollower, setRowsFollower] = useState([]);
  const [followersPanelOpen, setFollowersPanelOpen] = useState(false);
  const [followersData, setFollowersData] = useState([]);
  const [signalLoginForPanel, setSignalLoginForPanel] = useState(null);
  const [visibilitySwitchValue, setVisibilitySwitchValue] = useState(false);

  // Reusable modal configs for notification and form modal
  const [modalConfig, setModalConfig] = useState({
    open: false,
    iconType: "info",
    title: "",
    message: "",
    onAcceptLabel: "OK",
    onRefuseLabel: undefined,
    onContinueLabel: undefined,
    onAccept: undefined,
    onRefuse: undefined,
    onContinue: undefined,
    onClose: () => setModalConfig((prev) => ({ ...prev, open: false })),
  });

  const [formModalConfig, setFormModalConfig] = useState({
    open: false,
    title: "",
    fields: [],
    onSubmit: null,
    onCancel: null,
    type: "form",
    confirmLabel: "Save",
    cancelLabel: "Cancel",
    onClose: () => closeFormModal(),
  });

  const [copyToast, setCopyToast] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const handleCloseSignalsMenu = useCallback(() => {
    setAnchorElSignals(null);
    setSelectedSignalRow(null);
  }, []);

  const handleCloseFollowersMenu = useCallback(() => {
    setAnchorElFollowers(null);
    setSelectedFollowerRow(null);
  }, []);

  // Handlers for opening and closing modals
  const closeFormModal = () => setFormModalConfig((prev) => ({ ...prev, open: false }));
  // Helpers of NotificationModal (success and error with retry)
  // Helpers of NotificationModal (unified success/error)
  const openSuccessModal = (afterClose) =>
    setModalConfig({
      open: true,
      iconType: "success",
      title: "The operation was successful",
      message: "",
      onAcceptLabel: undefined,
      onRefuseLabel: undefined,
      onContinueLabel: "Continue",
      onAccept: undefined,
      onRefuse: undefined,
      onContinue: () => {
        if (afterClose) afterClose();
        setModalConfig((prev) => ({ ...prev, open: false }));
      },
      onClose: () =>
        setModalConfig((prev) => ({
          ...prev,
          open: false,
        })),
    });

  const getEndpointMessage = (error, fallback = "Server error") => {
    return (
      error?.response?.data?.message ||
      error?.response?.data?.data?.message ||
      fallback
    );
  };

  const openErrorModal = (message) =>
    setModalConfig({
      open: true,
      iconType: "error",
      title: message || "Server error",
      message: "",
      onAcceptLabel: "OK",
      onRefuseLabel: undefined,
      onContinueLabel: undefined,
      onAccept: () =>
        setModalConfig((prev) => ({
          ...prev,
          open: false,
        })),
      onRefuse: undefined,
      onContinue: undefined,
      onClose: () =>
        setModalConfig((prev) => ({
          ...prev,
          open: false,
        })),
    });

  // Fetch groups on mount
  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const res = await adminMethods.getGroups();

        if (res.status === 200) {
          setAvailableGroups(res.data.data);
        } else {
          setAvailableGroups([]);
          const msg = res.data?.message || "Failed to load groups";
          openErrorModal(msg, fetchGroups); // optional retry
        }
      } catch (error) {
        console.error("Error loading groups", error);
        setAvailableGroups([]);
        openErrorModal(
          "Failed to load groups. Please try again.",
          fetchGroups     // retry from button "Try again"
        );
      }
    };

    fetchGroups();
  }, []);

  const RequestAccounts = useCallback(async (forced = false) => {
    setLoading(true);
    try {
      let response;

      // SIGNALS
      if (tabstate === 0 || forced) { // Signal Accounts Request
        response = await adminMethods.accountsService({
          rol_filter: "Signal",
          search_filter: filterSignal.search,
          groups_filter:
            !filterSignal.groups || filterSignal.groups.length === 0
              ? "All"
              : filterSignal.groups.includes("All")
                ? "All"
                : filterSignal.groups.join(","),
          activity_filter: filterSignal.activity,
          exact_search: filterSignal.exact_search,
        });

        if (response.status === 200) {
          const storedAccounts = response.data.data.information_admin;
          if (
            storedAccounts &&
            Array.isArray(storedAccounts) &&
            storedAccounts.length > 0
          ) {
            const actualRows = storedAccounts.map((account, index) => ({
              id: index,
              login: account.login,
              name: account.name,
              group: account.group,
              available_in_mt: account.available_in_mt ? "Yes" : "No",
              status_of_ib_account: account.status_of_ib_account
                ? "Found"
                : "Not Found",
              currency: account.currency,
              strategy_name: account.strategy_name,
              strategy_description: account.strategy_description,
              strategy_type: account.strategy_type,
              fee_value_percentage: account.fee_value_percentage,
              total_followers: account.total_followers,
              visibility: account.visibility ? "Yes" : "No",
              drawdown: account.drawdown,
              ib_account: account.ib_account,
              max_drawdown: account.max_drawdown,
              disable_on_drawdown: account.disable_on_drawdown ? "Yes" : "No",
              followers: account.followers || [],
            }));
            setRowsSignal(actualRows);
          } else {
            setRowsSignal(null);
          }
        } else {
          const msg = response.data?.message ?? "Server error";
          setError(msg);
          setRowsSignal(null);
          openErrorModal(msg, () => RequestAccounts(true));
        }
      }

      // FOLLOWERS
      if (tabstate === 1 || forced) {
        response = await adminMethods.accountsService({
          rol_filter: "Follower",
          search_filter: filterFollower.search,
          groups_filter:
            !filterFollower.groups || filterFollower.groups.length === 0
              ? "All"
              : filterFollower.groups.includes("All")
                ? "All"
                : filterFollower.groups.join(","),
          activity_filter: filterFollower.activity,
          exact_search: filterFollower.exact_search,
        });

        if (response.status === 200) {
          const storedAccounts = response.data.data.information_admin;
          if (
            storedAccounts &&
            Array.isArray(storedAccounts) &&
            storedAccounts.length > 0
          ) {
            const actualRows = storedAccounts.map((account, index) => ({
              id: index,
              login: account.login,
              name: account.name,
              group: account.group,
              available_in_mt: account.available_in_mt ? "Yes" : "No",
              currency: account.currency,
              signal_login: account.signal_login,
              copy_mode: account.copy_mode,
              risk_ratio: account.risk_ratio,
              min_lot_size: account.min_lot_size,
              max_lot_size: account.max_lot_size,
              drawdown_mode: account.drawdown_mode,
              max_drawdown: account.max_drawdown,
              drawdown: account.drawdown,
              following_date: account.following_date,
              modification_date: account.modification_date,
              visibility_follower: account.visibility_follower ? "Yes" : "No",
            }));
            setRowsFollower(actualRows);
          } else {
            setRowsFollower(null);
          }
        } else {
          const msg = response.data?.message ?? "Server error";
          setError(msg);
          setRowsFollower(null);
          openErrorModal(msg, () => RequestAccounts(true));
        }
      }
    } catch (error) {
      console.error(error);
      setError("Failed to Request accounts");
      setRowsSignal(null);
      setRowsFollower(null);
      openErrorModal(
        "Failed to connect to server. Please check your connection.",
        () => RequestAccounts(true)
      );
    }
    setLoading(false);
  }, [tabstate, filterSignal, filterFollower]);

  // It runs as soon as the page loads (the first time)
  useEffect(() => {
    const fetch = async () => {
      await RequestAccounts(true);
    };
    fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // CRUD Handlers
  // For signals

  const handleEditSignal = () => {
    handleCloseSignalsMenu();
    handleOpenEditSignalForm(selectedSignalRow);
  };

  const handleMenuDeleteSignal = () => {
    handleCloseSignalsMenu();
    if (!selectedSignalRow) return;

    setModalConfig({
      open: true,
      iconType: "warning",
      title: "Are you sure you want to delete this signal?",
      message:
        "If the account has followers, they will be automatically unlinked",
      onAcceptLabel: "Yes, delete",
      onRefuseLabel: "No, back",
      onContinueLabel: undefined,
      onAccept: () => handleDeleteSignal(selectedSignalRow),
      onRefuse: undefined,
      onContinue: undefined,
      onClose: () =>
        setModalConfig(prev => ({
          ...prev,
          open: false,
        })),
    });
  };

  const handleDeleteSignal = async (row) => {
    const retryFn = () => handleDeleteSignal(row);

    try {
      const payload = {
        rol: "Signal",
        login: row.login,
      };

      const res = await adminMethods.deleteAccounts(payload); // AdminDeleteAccountsMethod

      if (res.status === 200) {
        const openOrders = res.data?.data?.open_orders ?? 0;

        if (openOrders > 0) {
          // special message if I had open orders
          setModalConfig({
            open: true,
            iconType: "warning",
            title: "Signal deleted with open orders",
            message: `This signal had ${openOrders} open orders when it was deleted.`,
            onAcceptLabel: "OK",
            onRefuseLabel: undefined,
            onContinueLabel: undefined,
            onAccept: undefined,
            onRefuse: undefined,
            onContinue: undefined,
            onClose: () =>
              setModalConfig((prev) => ({
                ...prev,
                open: false,
              })),
          });
        } else {
          // modal of success estándar
          openSuccessModal(() => RequestAccounts(true));
        }

        // refresh table
        RequestAccounts(true);
      } else {
        const msg = res.data?.message || "Error deleting signal";
        openErrorModal(msg, retryFn);
      }
    } catch (e) {
      console.error(e);
      openErrorModal(getEndpointMessage(e, "Failed to delete signal. Please try again."), retryFn);
    }
  };

  const navigate = useNavigate();
  //const [profile, setProfile] = useState(null);
  const handleGoToProfile = () => {
    handleCloseSignalsMenu();
    if (selectedSignalRow && selectedSignalRow.login) {
      // Change the route to the admin page by passing the login
      navigate(`/admin/signal-profile/${selectedSignalRow.login}`, { state: { signalLogin: selectedSignalRow.login, hideHistorical: true } });
      console.log('Navegando al perfil del login:', selectedSignalRow.login);

    }
  };
  const handleShowFollowers = () => {
    if (!selectedSignalRow) {
      handleCloseSignalsMenu();
      return;
    }
    const followers = selectedSignalRow.followers || [];
    // Save the Signal login for the panel title
    setSignalLoginForPanel(selectedSignalRow.login);
    setFollowersData(followers);
    setFollowersPanelOpen(true);
    handleCloseSignalsMenu();
    console.log("Show Followers", selectedSignalRow.login, followers);
  };

  const signalColumnsToShow = followersPanelOpen
    ? SIGNAL_COLUMNS.filter(col => col.field !== "actions")
    : SIGNAL_COLUMNS;


  const handleReviewCopies = () => {
    handleCloseSignalsMenu();
    // Logic for reviewing copies
    console.log('Review Copies', selectedSignalRow);
  };

  const handleCloseCopyToast = useCallback(() => {
    setCopyToast((prev) => ({ ...prev, open: false }));
  }, []);

  const handleGenerateLinkToFollow = useCallback(async () => {
    const signalLogin = selectedSignalRow?.login;

    handleCloseSignalsMenu();

    if (!signalLogin) return;

    try {
      const encryptedLogin = Encryptor.toBase64(String(signalLogin));
      const sharedLink = `${window.origin}/shared_signal_link/${encryptedLogin}`;

      await navigator.clipboard.writeText(sharedLink);

      setCopyToast({
        open: true,
        message: "Link copied to clipboard",
        severity: "success",
      });
    } catch (error) {
      console.error("Error copying follow link:", error);

      setCopyToast({
        open: true,
        message: "Unable to copy link",
        severity: "error",
      });
    }
  }, [selectedSignalRow, handleCloseSignalsMenu]);

  const handleGoToLogs = useCallback(() => {
    const selectedLogin = selectedSignalRow?.login ?? selectedFollowerRow?.login;

    if (anchorElSignals) {
      handleCloseSignalsMenu();
    }

    if (anchorElFollowers) {
      handleCloseFollowersMenu();
    }

    if (!selectedLogin) return;

    navigate("/admin/logs", {
      state: {
        presetSearch: String(selectedLogin),
        autoSearch: true,
      },
    });
  }, [
    selectedSignalRow,
    selectedFollowerRow,
    anchorElSignals,
    anchorElFollowers,
    handleCloseFollowersMenu,
    navigate,
  ]);

  const handleLoadStatements = () => {
    handleCloseSignalsMenu();
    // Logic for loading statements
    console.log('Load Statements', selectedSignalRow);
  };

  // Open Signal edit modal
  const handleOpenEditSignalForm = (row) => {
    if (!row) return;

    const isVisible = row.visibility === "Yes";
    setVisibilitySwitchValue(isVisible);

    setFormModalConfig({
      open: true,
      title: `Edit Signal ${row.login}`,
      message: "",
      type: "form",
      mode: "signal",
      variant: "edit",
      confirmLabel: "Save Changes",
      cancelLabel: "Cancel",
      fields: [
        {
          name: "strategy_name",
          label: "Strategy Name",
          value: row.strategy_name || "",
          required: true,
        },
        {
          name: "strategy_description",
          label: "Strategy Description",
          value: row.strategy_description || "",
          multiline: true,
          minRows: 3,
        },
        {
          name: "strategy_type",
          label: "Strategy Type",
          type: "select",
          value: row.strategy_type || "ExpertAdvisor",
          options: [
            { value: "ExpertAdvisor", label: "Expert Advisor" },
            { value: "Human", label: "Human" },
            { value: "Mixed", label: "Mixed" },
          ],
        },
        {
          name: "fee_value_percentage",
          label: "Fee (in percentage)",
          type: "number",
          value: row.fee_value_percentage.toString(),
        },
        {
          name: "ib_account",
          label: "IB Account",
          value: row.ib_account ?? "",
        },
        {
          name: "drawdown_mode",
          label: "Drawdown Protection",
          type: "select",
          value: row.drawdown || "NoDrawdown",
          options: [
            { value: "NoDrawdown", label: "No Drawdown" },
            { value: "InEquity", label: "In Equity" },
            { value: "InBalance", label: "In Balance" },
          ],
        },
        {
          name: "drawdown_amount",
          label: "Drawdown Amount",
          type: "number",
          value: row.max_drawdown ?? "",
        },
        {
          name: "disable_on_drawdown",
          label: "Disable on Drawdown",
          type: "select",
          value: row.disable_on_drawdown,
          options: [
            { value: "Yes", label: "Yes" },
            { value: "No", label: "No" },
          ],
        },
      ],
      onSubmit: (formData) => handleSubmitEditSignal(row, formData),
      onClose: closeFormModal,
    });
  };

  // Submit of edit of Signal
  const handleSubmitEditSignal = async (row, formData) => {  // ← Add async (formData)
    const retryFn = () => handleSubmitEditSignal(row, formData);

    try {
      const payload = {
        rol: "Signal",
        login: row.login,
        strategy_name: formData.strategy_name || null,
        strategy_description: formData.strategy_description || null,
        strategy_type: formData.strategy_type || null,
        fee_value_percentage:
          formData.fee_value_percentage !== ""
            ? Number(formData.fee_value_percentage)
            : null,
        visibility: !!formData.visibility_switch,
        drawdown: formData.drawdown_mode || null,
        max_drawdown:
          formData.drawdown_amount !== ""
            ? Number(formData.drawdown_amount)
            : null,
        disable_on_drawdown: formData.disable_on_drawdown === "Yes",
        ib_account:
          formData.ib_account !== "" ? Number(formData.ib_account) : null,
      };

      const res = await adminMethods.editAccounts(payload);

      if (res.status === 200) {
        closeFormModal();
        openSuccessModal(() => RequestAccounts(true));
      } else {
        // error backend
        const msg = res.data?.message || "Failed to update signal";
        openErrorModal(msg, retryFn);
      }
    } catch (e) {
      console.error(e);
      // error de network / exception
      openErrorModal(getEndpointMessage(e, "Failed to update signal"), retryFn);
    }
  };

  // === FIELDS TO CREATE SIGNAL ===
  const getCreateSignalFields = () => [
    {
      name: "metatrader_login",
      label: "Metatrader Login Number",
      placeholder: "Enter the login number",
      type: "number",
      value: "",
    },
    {
      name: "strategy_name",
      label: "Strategy Name",
      value: "",
    },
    {
      name: "strategy_description",
      label: "Strategy Description",
      value: "",
    },
    {
      name: "strategy_type",
      label: "Strategy Type",
      type: "select",
      value: "ExpertAdvisor",
      options: [
        { value: "ExpertAdvisor", label: "Expert Advisor" },
        { value: "Human", label: "Human" },
        { value: "Mixed", label: "Mixed" },
      ],
    },
    {
      name: "fee_value_percentage",
      label: "Fee (in percentage)",
      type: "number",
      value: "",
    },
    {
      name: "ib_account",
      label: "IB Account",
      type: "number",
      value: "",
    },
    {
      name: "drawdown_mode",
      label: "Drawdown Protection",
      type: "select",
      value: "NoDrawdown",
      options: [
        { value: "NoDrawdown", label: "No Drawdown" },
        { value: "InEquity", label: "In Equity" },
        { value: "InBalance", label: "In Balance" },
      ],
    },
    {
      name: "drawdown_amount",
      label: "Drawdown Amount",
      type: "number",
      value: "",
    },
    {
      name: "disable_on_drawdown",
      label: "Disable on Drawdown",
      type: "select",
      value: "Yes",
      options: [
        { value: "Yes", label: "Yes" },
        { value: "No", label: "No" },
      ],
    },
  ];

  // === open modal CREATE SIGNAL ===
  const handleCreateSignal = () => {
    setVisibilitySwitchValue(true); // switch Visibility on for default
    setFormModalConfig({
      open: true,
      title: "Create Signal",
      message: "",
      type: "form",
      confirmLabel: "Save Changes",
      cancelLabel: "Cancel",
      mode: "signal",
      variant: "create",
      fields: getCreateSignalFields(),
      onSubmit: handleSubmitCreateSignal,
      onClose: closeFormModal,
    });
  };

  // SUBMIT CREATE SIGNAL
  const handleSubmitCreateSignal = async (formData) => {
    const retryFn = () => handleSubmitCreateSignal(formData);

    try {
      const res = await adminMethods.createSignalAccount(
        formData,
        !!formData.visibility_switch
      );

      if (res.status === 200) {
        closeFormModal();
        openSuccessModal(() => RequestAccounts(true));
      } else {
        // error backend
        const msg = res.data?.message || "Failed to create signal";
        openErrorModal(msg, retryFn);
      }
    } catch (e) {
      console.error(e);
      // error de network / exception
      openErrorModal(getEndpointMessage(e, "Failed to create signal. Please try again."), retryFn);
    }
  };

  // For followers

  const handleUnlinkFollower = () => {
    handleCloseFollowersMenu();
    if (!selectedFollowerRow) return;

    setModalConfig({
      open: true,
      iconType: "warning",
      title: "Are you sure you want to unlink this follower?",
      message: "",
      onAcceptLabel: "Yes, unlink",
      onRefuseLabel: "No, back",
      onContinueLabel: undefined,
      onAccept: () => handleDeleteFollower(selectedFollowerRow),
      onRefuse: undefined,
      onContinue: undefined,
      onClose: () =>
        setModalConfig((prev) => ({
          ...prev,
          open: false,
        })),
    });
  };

  const handleDeleteFollower = async (row) => {
    const retryFn = () => handleDeleteFollower(row);

    try {
      const payload = {
        rol: "Follower",
        login: row.login,
      };

      const res = await adminMethods.deleteAccounts(payload);

      if (res.status === 200) {
        openSuccessModal(() => RequestAccounts(true));
      } else {
        // error backend
        const msg = res.data?.message || "Error unlinking follower";
        openErrorModal(msg, retryFn);
      }
    } catch (e) {
      console.error(e);
      // error of network / exception
      openErrorModal(getEndpointMessage(e, "Failed to unlink follower. Please try again."), retryFn);
    }
  };

  // Handler to edit follower
  const handleEditFollower = () => {
    handleCloseFollowersMenu();
    handleOpenEditFollowerForm(selectedFollowerRow);
    console.log('Edit Follower', selectedFollowerRow);
  };

  const handleOpenEditFollowerForm = (row) => {
    if (!row) return;

    const isCopying = row.visibility_follower === "Yes";
    setVisibilitySwitchValue(isCopying); // Copying

    setFormModalConfig({
      open: true,
      title: `Edit Follower ${row.login}`,
      message: "",
      type: "form",
      mode: "follower",
      variant: "edit",
      confirmLabel: "Save Changes",
      cancelLabel: "Cancel",
      fields: [
        {
          name: "copy_mode",
          label: "Copy Mode",
          type: "select",
          value: row.copy_mode || "Proportional",
          options: [
            { value: "Proportional", label: "Proportional" },
            { value: "FixedLots", label: "FixedLots" },
            { value: 'Multiplier', label: 'Multiplier' },
          ],
        },
        {
          name: "risk_ratio",
          label: "Risk Ratio",
          type: "number",
          value: row.risk_ratio ?? "",
        },
        {
          name: "min_lot_size",
          label: "Min Lot Size",
          type: "number",
          value: row.min_lot_size ?? "",
        },
        {
          name: "max_lot_size",
          label: "Max Lot Size",
          type: "number",
          value: row.max_lot_size ?? "",
        },
        {
          name: "drawdown_mode",
          label: "Drawdown Protection",
          type: "select",
          value: row.drawdown || "NoDrawdown",
          options: [
            { value: "NoDrawdown", label: "No Drawdown" },
            { value: "InEquity", label: "In Equity" },
            { value: "InBalance", label: "In Balance" },
          ],
        },
        {
          name: "drawdown_amount",
          label: "Drawdown Amount",
          type: "number",
          value: row.max_drawdown ?? "",
        },
      ],
      onSubmit: (formData) => handleSubmitEditFollower(row, formData),
      onClose: closeFormModal,
    });
  };

  const handleSubmitEditFollower = async (row, formData) => {
    const retryFn = () => handleSubmitEditFollower(row, formData);

    try {
      const payload = {
        rol: "Follower",
        login: row.login,
        copy_mode: formData.copy_mode || null,
        risk_ratio:
          formData.risk_ratio !== "" ? Number(formData.risk_ratio) : null,
        min_lot_size:
          formData.min_lot_size !== "" ? Number(formData.min_lot_size) : null,
        max_lot_size:
          formData.max_lot_size !== "" ? Number(formData.max_lot_size) : null,
        visibility: !!formData.visibility_switch,
        drawdown: formData.drawdown_mode || null,
        max_drawdown:
          formData.drawdown_amount !== ""
            ? Number(formData.drawdown_amount)
            : null,
      };

      const res = await adminMethods.editAccounts(payload); // route Signal

      if (res.status === 200) {
        // success → close form and show success
        closeFormModal();
        openSuccessModal(() => RequestAccounts(true)); // refresh followers
      } else {
        const msg = res.data?.message || "Failed to update follower";
        openErrorModal(msg, retryFn);
      }
    } catch (e) {
      console.error(e);
      openErrorModal(getEndpointMessage(e, "Failed to update follower"), retryFn);
    }
  };

  // === FIELDS TO CREATE FOLLOWER ===
  const getCreateFollowerFields = () => [
    {
      name: "follower_login",
      label: "Follower Login",
      type: "number",
      value: "",
    },
    {
      name: "signal_login",
      label: "Signal Login",
      type: "number",
      value: "",
    },
    {
      name: "copy_mode",
      label: "Copy Mode",
      type: "select",
      value: "Proportional",
      options: [
        { value: "Proportional", label: "Proportional" },
        { value: "FixedLots", label: "FixedLots" },
        { value: 'Multiplier', label: 'Multiplier' },
      ],
    },
    {
      name: "risk_ratio",
      label: "Risk Ratio",
      type: "number",
      value: "1",
    },
    {
      name: "min_lot_size",
      label: "Min Lot Size",
      type: "number",
      value: "0",
    },
    {
      name: "max_lot_size",
      label: "Max Lot Size",
      type: "number",
      value: "0",
    },
    {
      name: "drawdown_mode",
      label: "Drawdown Protection",
      type: "select",
      value: "NoDrawdown",
      options: [
        { value: "NoDrawdown", label: "No Drawdown" },
        { value: "InEquity", label: "In Equity" },
        { value: "InBalance", label: "In Balance" },
      ],
    },
    {
      name: "drawdown_amount",
      label: "Drawdown Amount",
      type: "number",
      value: "",
    },
  ];

  // === open modal CREATE FOLLOWER ===
  const handleCreateFollower = () => {
    setVisibilitySwitchValue(true); // Copying on for default
    setFormModalConfig({
      open: true,
      title: "Create Follower",
      message: "",
      type: "form",
      confirmLabel: "Save Changes",
      cancelLabel: "Cancel",
      mode: "follower",
      variant: "create",
      fields: getCreateFollowerFields(),
      onSubmit: handleSubmitCreateFollower,
      onClose: closeFormModal,
    });
  };

  // SUBMIT CREATE FOLLOWER
  const handleSubmitCreateFollower = async (formData) => {
    const retryFn = () => handleSubmitCreateFollower(formData);

    try {
      const res = await adminMethods.createFollowerAccount(
        formData,
        !!formData.visibility_switch
      );

      if (res.status === 200) {
        closeFormModal();
        openSuccessModal(() => RequestAccounts(true));
      } else {
        // error backend
        const msg = res.data?.message || "Failed to create follower";
        openErrorModal(msg, retryFn);
      }
    } catch (e) {
      console.error(e);
      // error network / exception
      openErrorModal(getEndpointMessage(e, "Failed to create follower. Please try again."), retryFn);
    }
  };

  // Export handlers
  const handleExportClick = (event) => setAnchorEl(event.currentTarget);
  const handleExportClose = () => setAnchorEl(null);
  const handleExport = (type) => {
    const today = new Date();
    const formattedDate = today.toISOString().slice(0, 10).replace(/-/g, "");

    if (type === "csv") {
      if (tabstate === 0) ExportComponent.GenerateCSV(SIGNAL_COLUMNS.filter((c) => c.field !== "actions"), rowsSignal, `signal_account_list_${formattedDate}`);
      else ExportComponent.GenerateCSV(FOLLOWER_COLUMNS.filter((c) => c.field !== "actions"), rowsFollower, `follower_account_list_${formattedDate}`);
    }
    if (type === "pdf") {
      if (tabstate === 0) ExportComponent.GeneratePDF(SIGNAL_COLUMNS.filter((c) => c.field !== "actions"), rowsSignal, `signal_account_list_${formattedDate}`);
      else ExportComponent.GeneratePDF(FOLLOWER_COLUMNS.filter((c) => c.field !== "actions"), rowsFollower, `follower_account_list_${formattedDate}`);
    }
    if (type === "txt") {
      if (tabstate === 0) ExportComponent.GenerateTXT(SIGNAL_COLUMNS.filter((c) => c.field !== "actions"), rowsSignal, `signal_account_list_${formattedDate}`);
      else ExportComponent.GenerateTXT(FOLLOWER_COLUMNS.filter((c) => c.field !== "actions"), rowsFollower, `follower_account_list_${formattedDate}`);
    }

    handleExportClose();
  };

  return (
    <Paper elevation={2} sx={Styles.containerPaper}>
      <NotificationModal {...modalConfig} />
      <FormSystemModal
        {...formModalConfig}
        showVisibility={true}
        visibilityValue={visibilitySwitchValue}
        onChangeVisibility={(e) => setVisibilitySwitchValue(e.target.checked)}
        mode={formModalConfig.mode}
        variant={formModalConfig.variant}
      />
      <CopyLinkSnackbar
        open={copyToast.open}
        message={copyToast.message}
        severity={copyToast.severity}
        onClose={handleCloseCopyToast}
      />
      <Typography variant="h5" gutterBottom>
        Accounts Management
      </Typography>
      <Typography sx={{ fontSize: "15px", color: "#687B8E", mb: 2 }}>
        Take a look at all the Signals and Followers registered.
      </Typography>

      <TapComponent
        onTabstate={setTabstate}
        tapsContent={["SIGNALS", "FOLLOWERS"]}
      />

      {/* TAB SIGNALS */}
      <CustomTapPanelComponent value={tabstate} index={0}>
        {/* FILTERS AND BUTTONS UP */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 2,
            mb: 3,
            p: 2,
            bgcolor: "#F8FAFC",
            borderRadius: "10px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.03)"
          }}
        >

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "4px",
            }}
          >
            <Typography
              sx={{
                fontFamily: "Plus Jakarta Sans, sans-serif",
                fontWeight: 400,
                fontSize: "12px",
                lineHeight: 1.4,
                color: Colors.Secondary_700,
                marginTop: "-18px"
              }}
            >
              Search
            </Typography>
            <SearchBoxComponent
              value={filterSignal.search}
              handleSearchTerm={e => setFilterSignal({ ...filterSignal, search: e.target.value })}
              placeholderprops={"Login, Name or Strategy"}
            />

          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: "4px",
            }}
          >
            <CheckBoxToggle
              checked={filterSignal.exact_search}
              onChange={(checked) =>
                setFilterSignal({ ...filterSignal, exact_search: checked })
              }
              title="Exact"
              label=""
            />
          </Box>

          <FilterGroupsChips
            selectedGroups={filterSignal.groups}
            onChangeGroups={chips =>
              setFilterSignal({ ...filterSignal, groups: chips })
            }
            groups={availableGroups}
          />
          <SelectorSimple
            title="Activity (Optional)"
            value={filterSignal.activity}
            handleChange={e =>
              setFilterSignal({ ...filterSignal, activity: e.target.value })
            }
            options={ACTIVITY_OPTIONS}
            placeholder="Filter by activity"
            error={false}
            helperText=""
            name="activity"
          />
          <Button3px label={"Search"} color={Colors.Warning_50} backgroundColor={Colors.Error_500} type="submit" HandleOnClick={() => RequestAccounts()}>
            Search
          </Button3px>

          <Box
            sx={{
              marginLeft: "auto",
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <Button
              onClick={handleCreateSignal}
              sx={Styles.AddButton}
            >
              <IconComponent icon="add" size="16px" />
              <Typography
                sx={{
                  fontFamily: "Plus Jakarta Sans, sans-serif",
                  fontWeight: 700,
                  fontSize: "12px",
                  lineHeight: 1.4,
                  textAlign: "center",
                }}
              >
                Register Signal
              </Typography>
            </Button>
            <Button
              variant="outlined"
              sx={Styles.ExportButton}
              onClick={handleExportClick}
            >
              <IconComponent icon="download" size="12px" />
              &nbsp;Export
            </Button>

            <Menu anchorEl={anchorEl} open={exportOpen} onClose={handleExportClose}>
              <MuiMenuItem onClick={() => handleExport("csv")}>Export as CSV</MuiMenuItem>
              <MuiMenuItem onClick={() => handleExport("pdf")}>Export as PDF</MuiMenuItem>
              <MuiMenuItem onClick={() => handleExport("txt")}>Export as TXT</MuiMenuItem>
            </Menu>
          </Box>

        </Box>

        {/* TABLE AND SIDEBAR grouped in flex, below the filters/tabs */}
        <Box sx={{ display: "flex", flexDirection: "row", width: "100%", alignItems: "flex-start" }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {loading ? (
              <Box sx={Styles.loading}>
                <CircularProgress color="primary" />
              </Box>
            ) : (
              <Box sx={{ height: 600, width: "100%", marginTop: "0px" }}>

                <TableFrontPagination
                  rows={rowsSignal}
                  columns={signalColumnsToShow}
                  getRowClassName={params => {  // ← Function normal, NO useCallback
                    const vis = params.row.visibility;
                    const mt = params.row.available_in_mt;
                    return vis === "Yes" && mt === "No" ? "mui-row-error" : "";
                  }}
                />

                <Menu
                  anchorEl={anchorElSignals}
                  open={Boolean(anchorElSignals)}
                  onClose={handleCloseSignalsMenu}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                  <MuiMenuItem onClick={handleShowFollowers}>Show Followers</MuiMenuItem>
                  <MuiMenuItem onClick={handleGoToProfile}>Go to Profile</MuiMenuItem>
                  <MuiMenuItem onClick={handleEditSignal}>Edit Signal</MuiMenuItem>
                  {/* <MuiMenuItem onClick={handleReviewCopies}>Review Copies</MuiMenuItem> */}
                  <MuiMenuItem onClick={handleGenerateLinkToFollow}>Generate Link to Follow</MuiMenuItem>
                  <MuiMenuItem onClick={handleGoToLogs}>Go to Logs</MuiMenuItem>
                  {/* <MuiMenuItem onClick={handleLoadStatements}>Load Statements</MuiMenuItem> */}
                  <MuiMenuItem onClick={handleMenuDeleteSignal}>Delete Signal Provider</MuiMenuItem>
                </Menu>
              </Box>
            )}
          </Box>
          {/* SIDE PANEL aligned with the table */}
          <FollowersPanelSidebar
            open={followersPanelOpen}
            onClose={() => setFollowersPanelOpen(false)}
            signalLogin={signalLoginForPanel}
            followersData={followersData}
          />
        </Box>
      </CustomTapPanelComponent>

      {/* TAB FOLLOWERS */}
      <CustomTapPanelComponent value={tabstate} index={1}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 2,
            mb: 3,
            p: 2,
            bgcolor: "#F8FAFC",
            borderRadius: "10px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.03)"
          }}
        >

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "4px",
            }}
          >
            <Typography
              sx={{
                fontFamily: "Plus Jakarta Sans, sans-serif",
                fontWeight: 400,
                fontSize: "12px",
                lineHeight: 1.4,
                color: Colors.Secondary_700,
                marginTop: "-18px"
              }}
            >
              Search
            </Typography>
            <SearchBoxComponent
              value={filterFollower.search}
              handleSearchTerm={e => setFilterFollower({ ...filterFollower, search: e.target.value })}
              placeholderprops={"Login, Name or Strategy"}
            />
          </Box>

          <CheckBoxToggle
            checked={filterFollower.exact_search}
            onChange={(checked) =>
              setFilterFollower({ ...filterFollower, exact_search: checked })
            }
            title="Exact"
            label=""
          />

          <FilterGroupsChips
            selectedGroups={filterFollower.groups}
            onChangeGroups={chips =>
              setFilterFollower({ ...filterFollower, groups: chips })
            }
            groups={availableGroups}
          />
          <SelectorSimple
            title="Activity (Optional)"
            value={filterFollower.activity}
            handleChange={e =>
              setFilterFollower({ ...filterFollower, activity: e.target.value })
            }
            options={ACTIVITY_OPTIONS}
            placeholder="Filter by activity"
            error={false}
            helperText=""
            name="activity"
          />
          <Button3px label={"Search"} color={Colors.Warning_50} backgroundColor={Colors.Error_500} type="submit" HandleOnClick={() => RequestAccounts()}>
            Search
          </Button3px>

          <Box
            sx={{
              marginLeft: "auto",
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <Button
              onClick={handleCreateFollower}
              sx={Styles.AddButton}
            >
              <IconComponent icon="add" size="16px" />
              <Typography
                sx={{
                  fontFamily: "Plus Jakarta Sans, sans-serif",
                  fontWeight: 700,
                  fontSize: "12px",
                  lineHeight: 1.4,
                  textAlign: "center",
                }}
              >
                Link Follower
              </Typography>
            </Button>
            <Button
              variant="outlined"
              sx={Styles.ExportButton}
              onClick={handleExportClick}
            >
              <IconComponent icon="download" size="12px" />
              &nbsp;Export
            </Button>
            <Menu anchorEl={anchorEl} open={exportOpen} onClose={handleExportClose}>
              <MuiMenuItem onClick={() => handleExport("csv")}>Export as CSV</MuiMenuItem>
              <MuiMenuItem onClick={() => handleExport("pdf")}>Export as PDF</MuiMenuItem>
              <MuiMenuItem onClick={() => handleExport("txt")}>Export as TXT</MuiMenuItem>
            </Menu>
          </Box>

        </Box>
        {/* Table only, no sidebar here */}
        <Box style={{ height: 600, width: "100%", marginTop: "0px" }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
              <CircularProgress color="primary" />
            </Box>
          ) : (
            <>
              <TableFrontPagination
                rows={rowsFollower}
                columns={FOLLOWER_COLUMNS}
                getRowClassName={params => {
                  const vis = params.row.visibility_follower;
                  const mt = params.row.available_in_mt;
                  return vis === "Yes" && mt === "No" ? "mui-row-error" : "";
                }}
              />
              <Menu
                anchorEl={anchorElFollowers}
                open={Boolean(anchorElFollowers)}
                onClose={handleCloseFollowersMenu}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <MuiMenuItem onClick={handleEditFollower}>Edit Follower</MuiMenuItem>
                {/* <MuiMenuItem onClick={handleReviewCopies}>Review Copies</MuiMenuItem> */}
                <MuiMenuItem onClick={handleGoToLogs}>Go to Logs</MuiMenuItem>
                <MuiMenuItem onClick={handleUnlinkFollower}>Unlink Follower</MuiMenuItem>
              </Menu>
            </>
          )}
        </Box>
      </CustomTapPanelComponent>
    </Paper>
  );
};

export default AccountsScreen;